/** @jsxImportSource @emotion/react */
'use client'

import { css } from '@emotion/react'
import Typography from '@mui/material/Typography'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  borderRadiusTokens,
  colorTokens,
  spacingTokens,
} from '../../../styles/tokens'
import { type ShowcaseSlide, showcaseSlides } from './showcaseSlides'
import { styles } from './styles'

const carouselStyles = {
  // Spans the full width of the green panel so slides scroll edge-to-edge.
  root: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  viewport: css`
    overflow: hidden;
  `,
  container: css`
    display: flex;
    align-items: center;
  `,
  // Full-width track; padding lives here so content stays inset from the panel
  // edges (and isn't clipped) as the carousel auto-scrolls.
  slide: css`
    flex: 0 0 100%;
    min-width: 0;
    padding: 0 ${spacingTokens.sectionGap}px;
  `,
  content: css`
    width: 100%;
    max-width: 460px;
    margin: 0 auto;
    text-align: center;
  `,
  // Stat-card slides own their internal alignment, so no text-align here.
  cardSlide: css`
    width: 100%;
    max-width: 460px;
    margin: 0 auto;
  `,
  image: css`
    width: 100%;
    height: auto;
    display: block;
    border-radius: ${borderRadiusTokens.surface}px;
    box-shadow: -24px 32px 56px -24px rgba(20, 39, 27, 0.45);
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${spacingTokens.elementGap}px;
    padding-block: ${spacingTokens.cardGap * 1.5}px;
  `,
  heading: css`
    color: ${colorTokens.white};
  `,
  description: css`
    color: rgba(255, 255, 255, 0.85);
  `,
  dots: css`
    position: absolute;
    bottom: ${spacingTokens.sectionGap}px;
    right: ${spacingTokens.sectionGap}px;
    z-index: 1;
    display: flex;
    gap: ${spacingTokens.elementGap}px;
  `,
  dot: css`
    appearance: none;
    border: none;
    padding: 0;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${colorTokens.green400};
    cursor: pointer;
    transition: background-color 150ms ease, width 150ms ease;

    &[aria-current='true'] {
      width: 22px;
      background: ${colorTokens.white};
    }
  `,
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface SlideBodyProps {
  slide: ShowcaseSlide
  /** First slide is prioritised for LCP (only affects media slides). */
  priority: boolean
}

function SlideBody({ slide, priority }: SlideBodyProps) {
  if (slide.kind === 'card') {
    const Card = slide.component
    return (
      <div css={carouselStyles.cardSlide}>
        <Card />
      </div>
    )
  }

  return (
    <div css={carouselStyles.content}>
      <div css={carouselStyles.body}>
        <Typography
          variant="h2"
          component="h2"
          css={[styles.headingFont, carouselStyles.heading]}
        >
          {slide.heading}
        </Typography>
      </div>
      {slide.image && (
        <Image
          src={slide.image}
          alt={slide.heading}
          priority={priority}
          sizes="(min-width: 768px) 460px, 100vw"
          css={carouselStyles.image}
        />
      )}
      <div css={carouselStyles.body}>
        <Typography variant="body1" css={carouselStyles.description}>
          {slide.description}
        </Typography>
      </div>
    </div>
  )
}

/** Fisher-Yates shuffle returning a new array (does not mutate the input). */
function shuffle<T>(items: readonly T[]): T[] {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export default function ShowcaseCarousel() {
  const [reducedMotion] = useState(prefersReducedMotion)
  const autoplay = useRef(
    Autoplay({
      delay: 3500,
      stopOnInteraction: false,
      stopOnMouseEnter: false,
      stopOnFocusIn: false,
    }),
  )
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center' },
    reducedMotion ? [] : [autoplay.current],
  )
  const [selectedIndex, setSelectedIndex] = useState(0)
  // Start from the deterministic module order (matches SSR / first paint), then
  // shuffle on the client after mount so the sequence differs per load without
  // risking a hydration mismatch.
  const [slides, setSlides] = useState<ShowcaseSlide[]>(showcaseSlides)

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  )

  useEffect(() => {
    setSlides(shuffle(showcaseSlides))
  }, [])

  // Embla captured the DOM at its original order on init; resync it (and the
  // dots/scroll snaps) whenever the rendered slide order changes.
  useEffect(() => {
    emblaApi?.reInit()
  }, [emblaApi, slides])

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap())
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi])

  return (
    <div css={carouselStyles.root}>
      <section
        css={carouselStyles.viewport}
        ref={emblaRef}
        aria-roledescription="carousel"
        aria-label="Platform highlights"
      >
        <div css={carouselStyles.container}>
          {slides.map((slide, index) => (
            // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA APG carousel slide — role="group" pairs with aria-roledescription="slide" and has no native HTML equivalent
            <div
              key={slide.id}
              css={carouselStyles.slide}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}: ${
                slide.kind === 'media' ? slide.heading : slide.label
              }`}
            >
              <SlideBody slide={slide} priority={index === 0} />
            </div>
          ))}
        </div>
      </section>
      <div css={carouselStyles.dots}>
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            css={carouselStyles.dot}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === selectedIndex}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </div>
  )
}
